import { MaybeNil } from '../../utils/interfaces';
import {
  IConditionFieldsFragment,
  IFilterFieldsFragment,
  ILogicalOperatorFieldsFragment,
  IPredicateFieldsFragment,
} from '../../utils/apollo/graphqlSchema';
import { isNil } from 'lodash';
import produce from 'immer';
import { assertCondition } from '../../utils/types';

/**
 * Remove predicate / condition at the given index and removes the corresponding
 * logic ops.
 *
 * IMPORTANT: mutates the container object. Use only in immer/Redux context!
 */
export const removeWithLogicOps = <
  Collection extends IConditionFieldsFragment[] | IPredicateFieldsFragment[],
  CollectionKey extends 'predicates' | 'conditions',
>(
  container: { logicOps: ILogicalOperatorFieldsFragment[] } & {
    [P in CollectionKey]: Collection;
  },
  key: CollectionKey,
  index: number,
) => {
  const predicatesOrConditions = container[key];
  const beforeRemovalLength = predicatesOrConditions.length;
  assertCondition(index < beforeRemovalLength);
  predicatesOrConditions.splice(index, 1);

  if (beforeRemovalLength <= 1) {
    // one condition or predicate doesn't have logic ops
    assertCondition(container.logicOps.length === 0);
    return;
  }

  if (index < container.logicOps.length) {
    // remove logic ops to the right
    container.logicOps.splice(index, 1);
  } else {
    // last condition/predicate - remove logic ops to the left
    container.logicOps.splice(-1, 1); // -1 means last
  }
};

export const normalizeFilter = (filter: MaybeNil<IFilterFieldsFragment>) => {
  if (isNil(filter)) {
    return null;
  }
  const normalizedFilter = produce(filter, (draft) => {
    let conditionIndex = 0;
    while (conditionIndex < draft.conditions.length) {
      const condition = draft.conditions[conditionIndex];
      if (condition.predicates.length === 0) {
        removeWithLogicOps(draft, 'conditions', conditionIndex);
      } else {
        conditionIndex++;
      }
    }
  });
  if (normalizedFilter.conditions.length === 0) {
    return null;
  }
  return normalizedFilter;
};
